import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Award, TrendingUp, ChartBar, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import {
  PageShell, PageHeader, Pill, Loader, EmptyState, Alert,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import TerritorySignalsWidget from "@/components/altiora/TerritorySignalsWidget";

export default function BiLeader() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/bi/overview")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  return (
    <PageShell testid="bi-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA · BI Leader"
          title="Vue cabinet — benchmarks anonymisés"
          subtitle="Vue synthétique de votre cabinet : moyenne, médiane, distribution de chaque dimension du Score de Trajectoire, et classement anonymisé des conseillers. Aucune identité individuelle n'est révélée."
          testid="bi-header"
        />

        {error && <div className="mt-6"><Alert variant="danger">{error}</Alert></div>}
        {!data && !error && <Loader label="Compilation des benchmarks…" />}

        {/* Macro-pulse — Statistique Canada · toujours affiché même sans cabinet */}
        <div className="mt-6" data-testid="bi-territory-signals">
          <TerritorySignalsWidget />
        </div>

        {data && data.cabinet_size === 0 && (
          <EmptyState icon={Users}
            title="Aucun conseiller avec une trajectoire calculée."
            description={data.message || "Les benchmarks s'activent dès qu'un conseiller enregistre son premier snapshot."}
            testid="bi-empty"
          />
        )}

        {data && data.cabinet_size > 0 && (
          <>
            {/* Cabinet-wide summary */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bi-summary">
              <StatCard label="Conseillers actifs" value={data.cabinet_size} accent={NAVY} testid="bi-size" />
              <StatCard label="Score moyen" value={data.global_score.mean} suffix="/100" accent={GOLD} testid="bi-mean" />
              <StatCard label="Score médian" value={data.global_score.median} suffix="/100" accent={CHAMPAGNE} testid="bi-median" />
              <StatCard label="Confiance moyenne" value={data.trust_index.mean} suffix="/100" accent="#5B21B6" testid="bi-trust" />
            </div>

            {/* Dimension benchmarks */}
            <div className="mt-10">
              <h2 className="font-serif text-2xl tracking-tight mb-4" style={{ color: NAVY }}>
                Benchmarks par dimension
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bi-benchmarks">
                {Object.entries(data.benchmarks).map(([k, b]) => (
                  <BenchmarkRow key={k} dimKey={k} b={b} />
                ))}
              </div>
            </div>

            {/* Anonymised leaderboard */}
            <div className="mt-10">
              <h2 className="font-serif text-2xl tracking-tight mb-4 flex items-center gap-2" style={{ color: NAVY }}>
                <Trophy className="h-5 w-5" style={{ color: GOLD }} />
                Classement anonymisé (top {Math.min(10, data.leaderboard.length)})
              </h2>
              <div className="rounded-xl bg-white p-4" style={{ border: `1px solid ${CHAMPAGNE}33` }}
                   data-testid="bi-leaderboard">
                {data.leaderboard.map((a, i) => (
                  <div key={a.advisor}
                       className="flex items-center justify-between py-2 border-b last:border-b-0"
                       style={{ borderColor: `${CHAMPAGNE}22` }}
                       data-testid={`bi-leader-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full flex items-center justify-center font-serif"
                           style={{
                             background: i === 0 ? `${GOLD}22` : `${NAVY}0f`,
                             color: i === 0 ? "#7A5B14" : NAVY,
                             fontSize: 14,
                           }}>
                        {i + 1}
                      </div>
                      <div>
                        <div className="text-[13px]" style={{ color: NAVY }}>{a.advisor}</div>
                        <div className="text-[10px] font-mono" style={{ color: SLATE }}>
                          Confiance {a.trust_index}/100 · {a.date}
                        </div>
                      </div>
                    </div>
                    <div className="font-serif text-lg tabular-nums" style={{ color: NAVY }}>
                      {a.global_score}<span className="text-[11px] opacity-60">/100</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}

function StatCard({ label, value, suffix = "", accent, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: `1px solid ${CHAMPAGNE}33` }} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: SLATE }}>{label}</div>
      <div className="mt-1 font-serif text-3xl tabular-nums" style={{ color: accent }}>
        {value}<span className="text-sm opacity-60">{suffix}</span>
      </div>
    </div>
  );
}

function BenchmarkRow({ dimKey, b }) {
  const bar = Math.max(0, Math.min(100, b.mean));
  return (
    <div className="rounded-lg bg-white p-4" style={{ border: `1px solid ${CHAMPAGNE}33` }}
         data-testid={`bi-bench-${dimKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-medium" style={{ color: NAVY }}>{b.label}</div>
        <div className="text-[11px] font-mono" style={{ color: SLATE }}>
          {b.count} conseiller{b.count > 1 ? "s" : ""}
        </div>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: `${SLATE}22` }}>
        <motion.div
          className="h-full"
          initial={{ width: 0 }}
          animate={{ width: `${bar}%` }}
          transition={{ duration: 0.6 }}
          style={{ background: GOLD }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] font-mono" style={{ color: SLATE }}>
        <span>min {b.min}</span>
        <span>médiane {b.median}</span>
        <strong style={{ color: NAVY }}>moyenne {b.mean}</strong>
        <span>max {b.max}</span>
      </div>
    </div>
  );
}
